/**
 * Competition Draws — Documents → Order of Events & Programme panel.
 *
 * Owns the "Draw for skating order" workflow that used to live inside
 * the Draft Programme panel. After the owner-driven split (Feb 2026),
 * the Draft Programme can render without a draw and this panel is the
 * single place where the user creates / replaces / uploads the draw.
 *
 * Future work: an Area-Figure manual-entry sub-tile will land here
 * next, plus a per-figure-grade list once the figures draw model is
 * settled (see PRD upcoming tasks).
 *
 * Feb 2026 — creating the skating-order draw is ALWAYS a manual
 * action. The previous "auto-draw on Local/Regional/Area on first
 * open" behaviour was removed so the user is never surprised by a
 * draw appearing the moment they navigate to this tile.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download, Upload, FileEdit, AlertTriangle, Sparkles, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import FigureDrawPanel from './FigureDrawPanel';
import ReconcileWarningBanner from './ReconcileWarningBanner';
import ManualDrawDialog from './ManualDrawDialog';


export default function CompetitionDrawsPanel({
  competitionsWithOOS = [],
  competitionId = '',
}) {
  const comp = competitionId;
  const [drawExists, setDrawExists] = useState(false);
  const [drawInfo, setDrawInfo] = useState(null);
  const [creatingDraw, setCreatingDraw] = useState(false);
  const [downloadingBlankDraw, setDownloadingBlankDraw] = useState(false);
  const [uploadingDraw, setUploadingDraw] = useState(false);
  const [drawWarningOpen, setDrawWarningOpen] = useState(false);
  const [pendingDrawAction, setPendingDrawAction] = useState(null);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileApplying, setReconcileApplying] = useState(false);
  const [reconcileDiff, setReconcileDiff] = useState(null);
  const [reconcileRefresh, setReconcileRefresh] = useState(0);
  const [manualDrawOpen, setManualDrawOpen] = useState(false);

  // -------------------------------------------------------------------
  // Lifecycle / data
  // -------------------------------------------------------------------

  const checkDrawExists = async (compId) => {
    if (!compId) { setDrawExists(false); setDrawInfo(null); return null; }
    try {
      const res = await axios.get(
        `${API}/order-of-skate/${compId}/has-draw`,
        { withCredentials: true }
      );
      setDrawExists(res.data.exists || false);
      setDrawInfo(res.data.exists ? res.data : null);
      return res.data;
    } catch {
      setDrawExists(false);
      setDrawInfo(null);
      return null;
    }
  };

  useEffect(() => {
    checkDrawExists(comp);
  }, [comp]);

  // -------------------------------------------------------------------
  // Action handlers
  // -------------------------------------------------------------------

  const performSystemDraw = async () => {
    if (!comp) return;
    setCreatingDraw(true);
    try {
      await axios.post(
        `${API}/order-of-skate/${comp}/create-draw`, {}, { withCredentials: true }
      );
      toast.success('Skating order draw completed');
      await checkDrawExists(comp);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create draw');
    } finally {
      setCreatingDraw(false);
    }
  };

  const handleSystemDrawClick = () => {
    if (drawExists) {
      setPendingDrawAction('redraw');
      setDrawWarningOpen(true);
    } else {
      performSystemDraw();
    }
  };

  const performDownloadBlankWorksheet = async () => {
    if (!comp) return;
    setDownloadingBlankDraw(true);
    try {
      const response = await axios.get(
        `${API}/order-of-skate/${comp}/draw-worksheet?blank=1`,
        { withCredentials: true, responseType: 'blob' }
      );
      const disposition = response.headers['content-disposition'] || '';
      let filename = 'Skating_Order_Worksheet_Blank.xlsx';
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      if (match) filename = match[1];
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Blank draw worksheet downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download worksheet');
    } finally {
      setDownloadingBlankDraw(false);
    }
  };

  const handleDownloadBlankWorksheetClick = () => {
    if (drawExists) {
      setPendingDrawAction('blank-worksheet');
      setDrawWarningOpen(true);
    } else {
      performDownloadBlankWorksheet();
    }
  };

  const performUploadDraw = async (file) => {
    if (!comp || !file) return;
    setUploadingDraw(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API}/order-of-skate/${comp}/upload-draw`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Draw uploaded successfully');
      await checkDrawExists(comp);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload draw');
    } finally {
      setUploadingDraw(false);
    }
  };

  // -------------------------------------------------------------------
  // Misc
  // -------------------------------------------------------------------

  const formatDrawWhen = (info) => {
    if (!info?.created_at) return '';
    const dt = new Date(info.created_at);
    const dd = dt.getDate();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yyyy = dt.getFullYear();
    let h = dt.getHours();
    const min = String(dt.getMinutes()).padStart(2, '0');
    const sec = String(dt.getSeconds()).padStart(2, '0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${dd}/${mm}/${yyyy} ${h}:${min}:${sec} ${ampm}`;
  };

  const drawCreatorLabel = drawInfo?.created_by || 'unknown user';

  const proceedWithPendingAction = () => {
    const action = pendingDrawAction;
    setDrawWarningOpen(false);
    setPendingDrawAction(null);
    if (action === 'redraw') performSystemDraw();
    else if (action === 'blank-worksheet') performDownloadBlankWorksheet();
    else if (action === 'manual-draw') setManualDrawOpen(true);
  };

  const handleOpenManualDraw = () => {
    if (drawExists) {
      setPendingDrawAction('manual-draw');
      setDrawWarningOpen(true);
      return;
    }
    setManualDrawOpen(true);
  };

  const handleManualDrawSaved = async () => {
    await checkDrawExists(comp);
    setReconcileRefresh(r => r + 1);
  };

  // -------------------------------------------------------------------
  // Reconcile Draw (Feb 2026) — surgical post-draw entry updates
  // -------------------------------------------------------------------

  const openReconcileModal = async () => {
    if (!comp) return;
    setReconcileOpen(true);
    setReconcileLoading(true);
    setReconcileDiff(null);
    try {
      const res = await axios.get(
        `${API}/order-of-skate/${comp}/reconcile-draw-preview`,
        { withCredentials: true }
      );
      setReconcileDiff(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load diff');
      setReconcileOpen(false);
    } finally {
      setReconcileLoading(false);
    }
  };

  const applyReconcile = async () => {
    if (!comp) return;
    setReconcileApplying(true);
    try {
      const res = await axios.post(
        `${API}/order-of-skate/${comp}/reconcile-draw`,
        {},
        { withCredentials: true }
      );
      if (res.data?.in_sync) {
        toast.success('Draw was already in sync — no changes applied.');
      } else {
        const s = res.data?.summary || {};
        const parts = [];
        if (s.names_refreshed) parts.push(`${s.names_refreshed} refreshed`);
        if (s.appended) parts.push(`${s.appended} appended`);
        if (s.withdrawn) parts.push(`${s.withdrawn} withdrawn`);
        if (s.grade_moves) parts.push(`${s.grade_moves} moved`);
        toast.success(`Draw reconciled — ${parts.join(' · ') || 'no changes'}`);
      }
      setReconcileOpen(false);
      setReconcileDiff(null);
      setReconcileRefresh(r => r + 1);
      await checkDrawExists(comp);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply reconcile');
    } finally {
      setReconcileApplying(false);
    }
  };

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-stone-800 mb-2">Competition Draws</h3>
        <p className="text-sm text-muted-foreground">
          Generate or enter the draw information for this event.
        </p>
      </div>

      {comp && (
        <ReconcileWarningBanner
          competitionId={comp}
          refreshKey={reconcileRefresh}
        />
      )}

      {!comp && (
        <div className="rounded-lg border bg-stone-50 px-3 py-3 text-sm text-stone-600">
          Pick a competition above to continue.
          {competitionsWithOOS.length === 0 && (
            <span className="block text-xs text-amber-600 mt-1">
              No competitions with a generated Order of Skate found.
            </span>
          )}
        </div>
      )}

      {comp && !competitionsWithOOS.find(c => c.competition_id === comp) && (
        <div className="rounded-lg border bg-amber-50 px-3 py-3 text-sm text-amber-900">
          This competition doesn&apos;t have an Order of Skate yet. Generate one first.
        </div>
      )}

      {comp && competitionsWithOOS.find(c => c.competition_id === comp) && (
        <>
          {/* Figure Draw — sits ABOVE the Draw for Skating Order so
              user runs figures first (foot is usually drawn closer to
              event week, but groups are picked early). */}
          <FigureDrawPanel competitionId={comp} />
          {/* Draw for skating order */}
          <Card
            className="p-5 space-y-4 border-stone-200 bg-stone-50/50"
            data-testid="draw-options-tile"
          >
            <div>
              <h4 className="text-base font-semibold text-stone-800">Draw for skating order</h4>
              {drawExists && drawInfo && (
                <p className="text-sm text-stone-700 mt-1">
                  Draw was created on <span className="font-medium">{formatDrawWhen(drawInfo)}</span>{' '}
                  by <span className="font-medium">{drawCreatorLabel}</span>.
                </p>
              )}
            </div>

            {/* Reconcile — surgical post-draw entry updates */}
            {drawExists && (
              <div className="grid grid-cols-[240px_1fr] gap-4 items-center">
                <Button
                  variant="outline"
                  onClick={openReconcileModal}
                  className="w-[240px] border-stone-300 justify-start pl-4"
                  data-testid="reconcile-draw-btn"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Reconcile Draw
                </Button>
                <div>
                  <p className="text-sm font-medium text-stone-800">Apply entry changes without a re-draw</p>
                  <p className="text-xs text-stone-600">
                    Refresh skater information or grades after corrections, add late entries, and remove withdrawals.
                    Existing skaters keep their drawn positions; new entries are appended to the end of their event.
                  </p>
                </div>
              </div>
            )}

            {/* Option: System Draw */}
            <div className="grid grid-cols-[240px_1fr] gap-4 items-center">
              <Button
                onClick={handleSystemDrawClick}
                disabled={creatingDraw}
                className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-[240px] justify-start pl-4"
                data-testid="system-draw-btn"
              >
                <Sparkles className={`mr-2 h-4 w-4 ${creatingDraw ? 'animate-spin' : ''}`} />
                {creatingDraw ? 'Drawing...' : drawExists ? 'Redraw' : 'Complete Draw'}
              </Button>
              <div>
                <p className="text-sm font-medium text-stone-800">System Draw</p>
                <p className="text-xs text-stone-600">
                  Allow CompCreator to complete a random draw of skating order.
                  Default for Local and Regional competitions, available here for others.
                </p>
              </div>
            </div>

            {/* Option: Draw Worksheet */}
            <div className="grid grid-cols-[240px_1fr] gap-4 items-center">
              <div className="flex flex-col gap-2 w-[240px]">
                <Button
                  variant="outline"
                  onClick={handleDownloadBlankWorksheetClick}
                  disabled={downloadingBlankDraw}
                  className="w-[240px] justify-start pl-4"
                  data-testid="download-blank-worksheet-btn"
                >
                  <Download className={`mr-2 h-4 w-4 ${downloadingBlankDraw ? 'animate-pulse' : ''}`} />
                  {downloadingBlankDraw ? 'Downloading...' : 'Download Worksheet'}
                </Button>
                <label
                  className={`inline-flex items-center justify-start rounded-md border border-input bg-background pl-4 pr-4 py-2 text-sm font-medium cursor-pointer hover:bg-accent w-[240px] ${uploadingDraw ? 'opacity-60 pointer-events-none' : ''}`}
                  data-testid="upload-completed-draw-btn"
                >
                  <Upload className={`mr-2 h-4 w-4 ${uploadingDraw ? 'animate-pulse' : ''}`} />
                  {uploadingDraw ? 'Uploading...' : 'Upload Completed Draw'}
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        performUploadDraw(f);
                        e.target.value = '';
                      }
                    }}
                  />
                </label>
              </div>
              <div>
                <p className="text-sm font-medium text-stone-800">Draw Worksheet</p>
                <p className="text-xs text-stone-600">
                  Download a worksheet, update with skating order, upload to CompCreator.
                </p>
              </div>
            </div>

            {/* Option: Manual webform — Feb 2026 */}
            <div className="grid grid-cols-[240px_1fr] gap-4 items-center">
              <Button
                variant="outline"
                onClick={handleOpenManualDraw}
                className="w-[240px] justify-start pl-4"
                data-testid="manual-draw-entry-btn"
              >
                <FileEdit className="mr-2 h-4 w-4" />
                Input skating order
              </Button>
              <div>
                <p className="text-sm font-medium text-stone-800">Enter the draw manually</p>
                <p className="text-xs text-stone-600">
                  Walk through each event and set the order of skate for each skater via a webform.
                </p>
              </div>
            </div>
          </Card>
        </>
      )}

      <AlertDialog open={drawWarningOpen} onOpenChange={setDrawWarningOpen}>
        <AlertDialogContent data-testid="draw-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Draw has already been created
            </AlertDialogTitle>
            <AlertDialogDescription>
              A draw has already been created on{' '}
              <span className="font-medium">{formatDrawWhen(drawInfo)}</span>{' '}
              by <span className="font-medium">{drawCreatorLabel}</span>.{' '}
              <strong>Do you want to replace this draw?</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => { setPendingDrawAction(null); }}
              data-testid="draw-warning-no-btn"
            >
              No
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={proceedWithPendingAction}
              className="bg-[#5C4033] hover:bg-[#3d2a22] text-white"
              data-testid="draw-warning-yes-btn"
            >
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reconcile Diff Preview Modal */}
      <Dialog open={reconcileOpen} onOpenChange={setReconcileOpen}>
        <DialogContent className="max-w-2xl" data-testid="reconcile-diff-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-[#5C4033]" />
              Reconcile Draw — Preview
            </DialogTitle>
            <DialogDescription>
              Compares the live entries against the persisted draw and shows exactly what will change.
              Existing skater positions are preserved; new arrivals go to the end of their event.
            </DialogDescription>
          </DialogHeader>

          {reconcileLoading && (
            <div className="py-6 text-sm text-stone-600" data-testid="reconcile-loading">
              Computing diff…
            </div>
          )}

          {!reconcileLoading && reconcileDiff && reconcileDiff.in_sync && (
            <div
              className="rounded-md border bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
              data-testid="reconcile-in-sync"
            >
              Draw is already in sync — no changes to apply.
            </div>
          )}

          {!reconcileLoading && reconcileDiff && !reconcileDiff.in_sync && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto" data-testid="reconcile-diff-body">
              {/* Summary badges */}
              <div className="flex flex-wrap gap-2 text-xs">
                {reconcileDiff.summary?.names_refreshed > 0 && (
                  <span className="rounded-full bg-blue-100 px-3 py-1 text-blue-800">
                    {reconcileDiff.summary.names_refreshed} refreshed
                  </span>
                )}
                {reconcileDiff.summary?.appended > 0 && (
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800">
                    {reconcileDiff.summary.appended} appended
                  </span>
                )}
                {reconcileDiff.summary?.withdrawn > 0 && (
                  <span className="rounded-full bg-red-100 px-3 py-1 text-red-800">
                    {reconcileDiff.summary.withdrawn} withdrawn
                  </span>
                )}
                {reconcileDiff.summary?.grade_moves > 0 && (
                  <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800">
                    {reconcileDiff.summary.grade_moves} moved
                  </span>
                )}
              </div>

              {/* Names refreshed */}
              {reconcileDiff.names_refreshed?.length > 0 && (
                <section data-testid="reconcile-section-refreshed">
                  <h5 className="text-sm font-semibold text-stone-800 mb-2">Details refreshed</h5>
                  <ul className="space-y-1 text-xs text-stone-700">
                    {reconcileDiff.names_refreshed.map((c, i) => (
                      <li key={i} className="rounded bg-blue-50 px-2 py-1.5">
                        <span className="font-medium">{c.event_name || `Event #${c.event_number}`}:</span>{' '}
                        {c.fields_changed?.map(f => (
                          <span key={f} className="ml-1">
                            {f.replace('_', ' ')}: <span className="line-through opacity-70">{c.old?.[f] || '—'}</span>
                            {' → '}
                            <span className="font-medium">{c.new?.[f] || '—'}</span>
                          </span>
                        ))}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Appended */}
              {reconcileDiff.appended?.length > 0 && (
                <section data-testid="reconcile-section-appended">
                  <h5 className="text-sm font-semibold text-stone-800 mb-2">New entries appended</h5>
                  <ul className="space-y-1 text-xs text-stone-700">
                    {reconcileDiff.appended.map((c, i) => (
                      <li key={i} className="rounded bg-emerald-50 px-2 py-1.5">
                        <span className="font-medium">{c.event_name || `Event #${c.event_number}`}:</span>{' '}
                        {c.skater_display}
                        {c.club_abbreviation ? ` (${c.club_abbreviation})` : ''}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Withdrawn */}
              {reconcileDiff.withdrawn?.length > 0 && (
                <section data-testid="reconcile-section-withdrawn">
                  <h5 className="text-sm font-semibold text-stone-800 mb-2">Withdrawals</h5>
                  <ul className="space-y-1 text-xs text-stone-700">
                    {reconcileDiff.withdrawn.map((c, i) => (
                      <li key={i} className="rounded bg-red-50 px-2 py-1.5">
                        <span className="font-medium">{c.event_name || `Event #${c.event_number}`}:</span>{' '}
                        <span className="line-through">{c.skater_display}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Grade moves */}
              {reconcileDiff.grade_moves?.length > 0 && (
                <section data-testid="reconcile-section-moves">
                  <h5 className="text-sm font-semibold text-stone-800 mb-2">Grade moves</h5>
                  <ul className="space-y-1 text-xs text-stone-700">
                    {reconcileDiff.grade_moves.map((c, i) => (
                      <li key={i} className="rounded bg-amber-50 px-2 py-1.5">
                        <span className="font-medium">{c.skater_display}:</span>{' '}
                        {c.from_event_name || `Event #${c.from_event_number}`}
                        {' → '}
                        <span className="font-medium">
                          {c.to_event_name || `Event #${c.to_event_number}`}
                        </span>
                        <span className="text-stone-500"> (appended to end)</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReconcileOpen(false)}
              disabled={reconcileApplying}
              data-testid="reconcile-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={applyReconcile}
              disabled={reconcileApplying || reconcileLoading || (reconcileDiff?.in_sync ?? true)}
              className="bg-[#5C4033] hover:bg-[#3d2a22] text-white"
              data-testid="reconcile-confirm-btn"
            >
              {reconcileApplying ? 'Applying…' : 'Apply Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Input Skating Order — walk-through manual draw modal (Feb 2026) */}
      <ManualDrawDialog
        open={manualDrawOpen}
        onOpenChange={setManualDrawOpen}
        competitionId={comp}
        onSaved={handleManualDrawSaved}
      />
    </div>
  );
}
